import type {
  AchievementEntity,
  AchievementMetric,
  LangCoded,
} from "~/src/api";
import { Achievement, getAchievementImage, isOrg, isBadge } from "~/src/api";
import { getFileFromUrl } from "~/utils/image";

export const getSystemMetrics = (): AchievementMetric[] => [
  "system_account_age",
  "system_account_setup",
  "system_subscription_count",
  "system_combo_unlocked",
];

export const getCreatorMetrics = (): AchievementMetric[] => [
  "creator_chapter_count",
  "creator_comment_count",
  "creator_chapter_view",
  "creator_reaction_count",
  "creator_follow_count",
  "creator_rating_count",
  "creator_subscription_count",
  "creator_title_count",
];

export const getUserMetrics = (): AchievementMetric[] => [
  "user_chapter_comment_count",
  "user_chapter_view_count",
  "user_chapter_reaction_count",
  "user_title_follow_count",
  "user_organization_subscription_age",
];

export const hasCountParameter = (metric: AchievementMetric) =>
  [
    "creator_chapter_count",
    "creator_comment_count",
    "creator_chapter_view",
    "creator_reaction_count",
    "creator_follow_count",
    "creator_rating_count",
    "creator_subscription_count",
    "creator_title_count",
    "user_chapter_comment_count",
    "user_chapter_view_count",
    "user_chapter_reaction_count",
    "user_title_follow_count",
    "system_subscription_count",
  ].includes(metric);

export const hasAgeParameter = (metric: AchievementMetric) =>
  ["user_organization_subscription_age", "system_account_age"].includes(metric);

export interface AchievementForm {
  name: LangCoded;
  metric: AchievementMetric | null;
  parameters: {
    ageSeconds?: number;
    count?: number;
  };
  isHidden: boolean;
  organizationId?: string;
  badgeId?: string;
  image?: File | null;
  meta?: {
    id: string;
    version: number;
    updatedAt: number;
  };
}

export const getDefaultAchievementForm = (): AchievementForm => ({
  name: {},
  metric: null,
  parameters: {
    count: 1,
    ageSeconds: 60 * 60 * 24,
  },
  isHidden: false,
  organizationId: undefined,
  badgeId: undefined,
  image: undefined,
});

export const getAchievementForm = async (
  achievement: AchievementEntity,
): Promise<AchievementForm> => {
  const serverImg = getAchievementImage(achievement);
  const updatedAt =
    achievement.attributes.updatedAt || achievement.attributes.createdAt;
  const updated = new Date(updatedAt).valueOf();
  let image = undefined;
  if (serverImg) {
    image = await getFileFromUrl(serverImg, "achievement", updated);
  }

  return {
    name: achievement.attributes.name,
    metric: achievement.attributes.metric,
    isHidden: achievement.attributes.isHidden,
    parameters: achievement.attributes.parameters ?? {},
    organizationId: achievement.relationships.find(isOrg)?.id,
    badgeId: achievement.relationships.find(isBadge)?.id,
    image,
    meta: {
      id: achievement.id,
      version: achievement.attributes.version,
      updatedAt: updated,
    },
  };
};

function validateCreate(
  form: AchievementForm,
): asserts form is AchievementForm & { metric: AchievementMetric } {
  if (form.meta) throw new Error("Metric has already been created.");
  if (!form.metric)
    throw new Error("Metric is required to create an achievement.");
  if (hasCountParameter(form.metric) && (form.parameters.count ?? 0) <= 0)
    throw new Error("Count must be greater than 0.");
  if (hasAgeParameter(form.metric) && (form.parameters.ageSeconds ?? 0) <= 0)
    throw new Error("Age must be greater than 0.");
}

export async function createAchievement(
  form: AchievementForm,
): Promise<AchievementEntity> {
  validateCreate(form);
  const token = await getTokenOrThrow();

  let res = await Achievement.create(
    {
      name: form.name,
      metric: form.metric,
      parameters: form.parameters,
      organizationId: form.organizationId,
      badgeId: form.badgeId,
      isHidden: form.isHidden,
    },
    token,
  );

  if (form.image) {
    res = await Achievement.uploadImage(
      res.id,
      form.image,
      res.attributes.version,
      token,
    );
  }

  return res;
}

function validateUpdate(
  form: AchievementForm,
): asserts form is AchievementForm & {
  metric: AchievementMetric;
  meta: { id: string; version: number; updatedAt: number };
} {
  if (!form.meta) throw new Error("Metric cannot be updated without meta.");
  if (!form.metric)
    throw new Error("Metric is required to create an achievement.");
  if (hasCountParameter(form.metric) && (form.parameters.count ?? 0) <= 0)
    throw new Error("Count must be greater than 0.");
  if (hasAgeParameter(form.metric) && (form.parameters.ageSeconds ?? 0) <= 0)
    throw new Error("Age must be greater than 0.");
}

export async function updateAchievement(
  form: AchievementForm,
): Promise<AchievementForm> {
  validateUpdate(form);
  const token = await getTokenOrThrow();

  let res = await Achievement.update(
    form.meta.id,
    {
      name: form.name,
      // TODO: remove once BE
      metric: form.metric,
      parameters: form.parameters,
      organizationId: form.organizationId,
      badgeId: form.badgeId,
      isHidden: form.isHidden,
      version: form.meta.version,
    },
    token,
  );

  if (form.image && form.image.lastModified > form.meta.updatedAt) {
    res = await Achievement.uploadImage(
      res.id,
      form.image,
      res.attributes.version,
      token,
    );
  }

  return getAchievementForm(res);
}
